import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MoneyInput } from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck, Sparkles, Loader2, Package, ArrowUp, ArrowDown, GripVertical, FileText } from "lucide-react";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// 🆕 16/07/2026 — Wrapper Sortable per una singola riga di garanzia (drag & drop)
function SortableGuaranteeRow({ id, children, className, testId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    boxShadow: isDragging ? "0 8px 22px -8px rgba(79,70,229,0.35)" : undefined,
    zIndex: isDragging ? 30 : undefined,
    position: isDragging ? "relative" : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className={className} data-testid={testId}>
      {typeof children === "function" ? children({ attributes, listeners }) : children}
    </div>
  );
}
import { toast } from "sonner";

/**
 * Editor Garanzie di polizza legate al listino della Compagnia.
 *
 * Props:
 *  - companyId: string — id compagnia della polizza (necessario per fetch garanzie).
 *  - companyName: string — nome compagnia (visualizzato nei dialog).
 *  - coverages: array di { code, name, active, premium, company_guarantee_id?, imposte_percent?, ssn_percent?, buy_cost?, sell_cost? }
 *  - onChange: (newCoverages) => void
 *  - canSeeMargins: boolean — mostra colonne Acquisto/Vendita
 *  - testIdPrefix?: string — prefisso per data-testid (default "policy")
 */
export function PolicyGuaranteesEditor({
  companyId,
  companyName = "",
  coverages = [],
  onChange,
  canSeeMargins = false,
  commissionPercent = 0,
  agencyPctOfCommission = 100,
  agencyPctOfFee,
  collabPctOfAgency = 0,
  collabName = "",
  products = [],
  linkId = null,
  testIdPrefix = "policy",
  // 🆕 16/07/2026 — Callback opzionale per notificare i totali calcolati al parent
  onTotalsChange = null,
  // 🆕 20/07/2026 — Flag per escludere le Extra Fees dal totale mostrato nell'Estratto Conto
  // (restano SEMPRE visibili e conteggiate nel dettaglio polizza)
  excludeExtraFeesFromStatement = false,
  onExcludeExtraFeesFromStatementChange = null,
  // 🆕 20/07/2026 — Analogo flag per Comp. Fisso Broker
  excludeBrokerFixedFromStatement = false,
  onExcludeBrokerFixedFromStatementChange = null,
  // 🆕 Feb 2026 — Provvigione Bonus Multi-Garanzia (broker.multi_guarantee_bonus_percent).
  // Quando > 0 e la polizza ha >1 garanzia con net_premium > 0:
  //   totProvv = multiGuaranteeBonusPercent × Σ net_premium (sostituisce il calcolo per-riga)
  //   totFeeAg = totProvv × agencyPctOfCommission%
  multiGuaranteeBonusPercent = 0,
}) {
  const feePct = agencyPctOfFee ?? agencyPctOfCommission;
  // Con accordo broker↔compagnia: garanzia senza % propria = 0% (nessuna eredità dalla % polizza)
  const defaultRowPct = linkId ? 0 : (commissionPercent || 0);
  const [companyGuarantees, setCompanyGuarantees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Inline create form (dentro il picker)
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", imposte_percent: "", ssn_percent: "", buy_price: "", sell_price: "", notes: "" });
  const [savingNew, setSavingNew] = useState(false);
  // 🆕 15/07/2026 — Picker Prodotti da Catalogo Prodotti
  const [productsPickerOpen, setProductsPickerOpen] = useState(false);
  const [productsSelected, setProductsSelected] = useState(() => new Set());
  // 🆕 15/07/2026 — Override provvigione per garanzia dal link Partner↔Compagnia (accordi broker)
  const [linkGuaranteesMap, setLinkGuaranteesMap] = useState({});
  // 🆕 15/07/2026 — Toggle per escludere il campo "Vendita" dal Totale Generale Premio Lordo
  // Preferenza persistita in localStorage per singolo utente.
  const [excludeSellFromTotal, setExcludeSellFromTotal] = useState(() => {
    try { return localStorage.getItem("policy_guarantees_exclude_sell_from_total") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("policy_guarantees_exclude_sell_from_total", excludeSellFromTotal ? "1" : "0"); } catch { /* ignore */ }
  }, [excludeSellFromTotal]);

  const load = useCallback(async () => {
    if (!companyId) { setCompanyGuarantees([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/guarantees`);
      setCompanyGuarantees(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
      setCompanyGuarantees([]);
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // 🆕 15/07/2026 — Carica gli override provvigione per garanzia dal link Partner↔Compagnia
  useEffect(() => {
    if (!linkId) { setLinkGuaranteesMap({}); return; }
    api.get(`/partner-company-links/${linkId}/guarantees`)
      .then(({ data }) => {
        const map = {};
        for (const g of (data || [])) {
          if (g.guarantee_id) map[g.guarantee_id] = { commission_percent: g.commission_percent, fixed_cost: g.fixed_cost };
        }
        setLinkGuaranteesMap(map);
      })
      .catch(() => setLinkGuaranteesMap({}));
  }, [linkId]);

  // Set di guarantee_id già usate in polizza (per filtro)
  const usedIds = useMemo(() => {
    const s = new Set();
    for (const c of coverages) {
      if (c?.company_guarantee_id) s.add(c.company_guarantee_id);
    }
    return s;
  }, [coverages]);

  // Set di label (case-insensitive) già usate — per garanzie senza company_guarantee_id (retrocompat)
  const usedLabels = useMemo(() => {
    const s = new Set();
    for (const c of coverages) {
      const n = (c?.name || "").trim().toLowerCase();
      if (n) s.add(n);
    }
    return s;
  }, [coverages]);

  // Garanzie disponibili nel picker: escludi quelle già in polizza
  const availableGuarantees = useMemo(() => {
    return (companyGuarantees || []).filter((g) => {
      if (usedIds.has(g.id)) return false;
      const n = (g.name || "").trim().toLowerCase();
      if (n && usedLabels.has(n)) return false;
      return true;
    });
  }, [companyGuarantees, usedIds, usedLabels]);

  const addGuaranteeToPolicy = (g) => {
    const code = (g.name || "").toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
      .slice(0, 50) || `garanzia_${Date.now()}`;
    const newRow = {
      code,
      name: g.name,
      active: true,
      premium: "",
      company_guarantee_id: g.id,
      imposte_percent: g.imposte_percent ?? null,
      ssn_percent: g.ssn_percent ?? null,
      buy_cost: g.buy_price ?? null,
      sell_cost: g.sell_price ?? null,
      // 🆕 15/07/2026 — Snapshot flag visibilità campi Acquisto/Vendita in polizza
      has_buy_field: !!g.has_buy_field,
      has_sell_field: !!g.has_sell_field,
    };
    onChange([...(coverages || []), newRow]);
  };

  // 🆕 15/07/2026 — Aggiungi un prodotto dal Catalogo Prodotti come nuova riga garanzia
  const addProductToPolicy = (p) => {
    const codeBase = (p.code || p.name || "").toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
      .slice(0, 50) || `prodotto_${Date.now()}`;
    const newRow = {
      code: codeBase,
      name: p.name,
      active: true,
      premium: "",
      product_id: p.id,
      buy_cost: p.buy_cost ?? null,
      sell_cost: p.sell_cost ?? null,
    };
    onChange([...(coverages || []), newRow]);
  };

  const removeAt = (idx) => {
    const next = [...(coverages || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  // 🆕 16/07/2026 — Sposta una garanzia su/giù nell'elenco (ordinamento manuale)
  const moveAt = (idx, direction) => {
    const next = [...(coverages || [])];
    const target = idx + direction;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  // 🆕 16/07/2026 — Sensor DnD Kit per drag&drop garanzie
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 🆕 16/07/2026 — Emetti totali provvigione/fee al parent (Scadenze rate ha bisogno di questi valori)
  useEffect(() => {
    if (typeof onTotalsChange !== "function") return;
    // 🆕 Feb 2026 — Multi-Garanzia attiva? (bonus% > 0 && >1 garanzia con net > 0)
    const _bonusPct = parseFloat(multiGuaranteeBonusPercent) || 0;
    const _netsGt0 = (coverages || [])
      .map((c) => parseFloat(c.net_premium) || 0)
      .filter((n) => n > 0);
    const _multiActive = _bonusPct > 0 && _netsGt0.length > 1;
    let totProvv = 0;
    if (_multiActive) {
      // Provvigione totale = bonus% × Σ net (sostituisce il calcolo per-riga)
      totProvv = _netsGt0.reduce((s, n) => s + n, 0) * _bonusPct / 100;
    } else {
      for (const c of (coverages || [])) {
        const n = parseFloat(c.net_premium) || 0;
        const ov = c.company_guarantee_id ? linkGuaranteesMap[c.company_guarantee_id] : null;
        if (ov && ov.fixed_cost != null && Number(ov.fixed_cost) > 0) {
          totProvv += Number(ov.fixed_cost);
        } else if (ov && ov.commission_percent != null) {
          totProvv += n * Number(ov.commission_percent) / 100;
        } else {
          totProvv += n * defaultRowPct / 100;
        }
      }
    }
    const totFeeAg = totProvv * (agencyPctOfCommission || 0) / 100;
    const sumField = (field) => (coverages || []).reduce((s, c) => s + (parseFloat(c[field]) || 0), 0);
    const totBrokerFeeSplit = sumField("broker_fee_to_split");
    const totExtraFees = sumField("extra_fees");
    const totBrokerFixed = sumField("broker_fixed_fee");
    const totAgencyFromBrokerFee = totBrokerFeeSplit * (feePct || 0) / 100;
    const totFeeAgWithFees = totFeeAg + totAgencyFromBrokerFee + totExtraFees + totBrokerFixed;
    // 🆕 16/07/2026 — Margine (auto) totale = Σ (Vendita − Acquisto) sui prodotti dal catalogo
    let totMargin = 0;
    for (const c of (coverages || [])) {
      if (!c.product_id) continue;
      const buy = parseFloat(c.buy_cost);
      const sell = parseFloat(c.sell_cost);
      if (!Number.isFinite(buy) || !Number.isFinite(sell)) continue;
      totMargin += (sell - buy);
    }
    onTotalsChange({
      totProvv,
      totFeeAg, // Σ Provv. Agenzia (solo provvigione)
      totFeeAgWithFees, // Σ Provv. Agenzia + Fee accessorie
      totAgencyFromBrokerFee,
      totBrokerFeeSplit,
      totExtraFees,
      totBrokerFixed,
      totMargin, // 🆕 Σ Margine (auto) sui prodotti dal catalogo
      multiGuaranteeActive: _multiActive,
      multiGuaranteeBonusAmount: _multiActive ? Math.round(totProvv * 100) / 100 : 0,
    });
  }, [coverages, linkGuaranteesMap, commissionPercent, defaultRowPct, agencyPctOfCommission, feePct, multiGuaranteeBonusPercent, onTotalsChange]);

  // Calcolo automatico Premio Netto da Premio Lordo + %Imposte + %SSN
  // Formula ITA: Lordo = Netto * (1 + (imposte% + ssn%)/100) → Netto = Lordo / (1 + …)
  const computeNet = (grossVal, imposte_percent, ssn_percent) => {
    const gross = parseFloat(grossVal);
    if (!Number.isFinite(gross) || gross <= 0) return null;
    const tot = (parseFloat(imposte_percent) || 0) + (parseFloat(ssn_percent) || 0);
    if (tot <= 0) return Math.round(gross * 100) / 100;
    const net = gross / (1 + tot / 100);
    return Math.round(net * 100) / 100;
  };
  // 🆕 Formula inversa: Netto → Lordo
  const computeGross = (netVal, imposte_percent, ssn_percent) => {
    const net = parseFloat(netVal);
    if (!Number.isFinite(net) || net <= 0) return null;
    const tot = (parseFloat(imposte_percent) || 0) + (parseFloat(ssn_percent) || 0);
    const gross = net * (1 + tot / 100);
    return Math.round(gross * 100) / 100;
  };

  const updatePremium = (idx, val) => {
    const next = [...(coverages || [])];
    const row = next[idx];
    const newPremium = val === "" ? "" : Number(val);
    const netAuto = computeNet(newPremium, row?.imposte_percent, row?.ssn_percent);
    next[idx] = { ...row, premium: newPremium, net_premium: netAuto, active: true };
    onChange(next);
  };

  // 🆕 15/07/2026 — Aggiornamento Netto con auto-calcolo del Lordo (bidirezionale)
  const updateNet = (idx, val) => {
    const next = [...(coverages || [])];
    const row = next[idx];
    const newNet = val === "" ? "" : Number(val);
    const grossAuto = computeGross(newNet, row?.imposte_percent, row?.ssn_percent);
    next[idx] = { ...row, net_premium: newNet, premium: grossAuto != null ? grossAuto : row?.premium, active: true };
    onChange(next);
  };

  // 🆕 15/07/2026 — Campi Acquisto/Vendita/altri editabili per singola garanzia in polizza
  const updateField = (idx, field, val) => {
    const next = [...(coverages || [])];
    next[idx] = { ...next[idx], [field]: val === "" || val == null ? null : Number(val) };
    onChange(next);
  };

  const totale = useMemo(() => {
    // 🆕 15/07/2026 (aggiornato) — Totale Generale Premio Lordo = Σ premio + Σ Vendita + Σ Fee Broker (rip.) + Σ Spese Gest. Broker + Σ Extra Fees.
    // NB: NON include "Comp. Fisso Broker €" (solo provvigioni, non entra nel totale polizza).
    return (coverages || []).reduce((s, c) => {
      const prem = parseFloat(c.premium) || 0;
      const sell = excludeSellFromTotal ? 0 : (parseFloat(c.sell_cost) || 0);
      const feeBrokerSplit = parseFloat(c.broker_fee_to_split) || 0;
      const mgmtFee = parseFloat(c.broker_management_cost) || 0;
      const extraFees = parseFloat(c.extra_fees) || 0;
      return s + prem + sell + feeBrokerSplit + mgmtFee + extraFees;
    }, 0);
  }, [coverages, excludeSellFromTotal]);

  const totaleSell = useMemo(() => {
    return (coverages || []).reduce((s, c) => s + (parseFloat(c.sell_cost) || 0), 0);
  }, [coverages]);

  // 🆕 15/07/2026 — Somme dei nuovi componenti inclusi nel totale (per il breakdown visivo)
  const totaleFeeBrokerSplit = useMemo(() => (coverages || []).reduce((s, c) => s + (parseFloat(c.broker_fee_to_split) || 0), 0), [coverages]);
  const totaleBrokerMgmt = useMemo(() => (coverages || []).reduce((s, c) => s + (parseFloat(c.broker_management_cost) || 0), 0), [coverages]);
  const totaleExtraFees = useMemo(() => (coverages || []).reduce((s, c) => s + (parseFloat(c.extra_fees) || 0), 0), [coverages]);
  const totalePremiOnly = useMemo(() => (coverages || []).reduce((s, c) => s + (parseFloat(c.premium) || 0), 0), [coverages]);

  const openPicker = () => {
    setCreating(false);
    setNewForm({ name: "", imposte_percent: "", ssn_percent: "", buy_price: "", sell_price: "", notes: "" });
    setPickerOpen(true);
  };

  const saveNewGuarantee = async () => {
    const name = (newForm.name || "").trim();
    if (!name) { toast.error("Il nome della garanzia è obbligatorio"); return; }
    if (!companyId) { toast.error("Seleziona prima una compagnia sulla polizza"); return; }
    setSavingNew(true);
    try {
      const parseNum = (v) => (v === "" || v == null) ? null : parseFloat(v);
      const payload = {
        name,
        imposte_percent: parseNum(newForm.imposte_percent),
        ssn_percent: parseNum(newForm.ssn_percent),
        buy_price: parseNum(newForm.buy_price),
        sell_price: parseNum(newForm.sell_price),
        notes: (newForm.notes || "").trim() || null,
      };
      const { data: created } = await api.post(`/companies/${companyId}/guarantees`, payload);
      // Aggiorna elenco locale e aggiungi immediatamente alla polizza
      setCompanyGuarantees((prev) => [...prev, created].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      addGuaranteeToPolicy(created);
      toast.success(`Garanzia "${name}" creata e aggiunta alla polizza`);
      setCreating(false);
      setNewForm({ name: "", imposte_percent: "", ssn_percent: "", buy_price: "", sell_price: "", notes: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingNew(false); }
  };

  const canAdd = !!companyId;

  return (
    <div className="sm:col-span-2 rounded-lg border-2 border-indigo-300 bg-indigo-50/50 p-3" data-testid={`${testIdPrefix}-guarantees-manager`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <Label className="text-[12px] text-indigo-900 font-bold uppercase tracking-wide m-0 flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4" /> Gestione Garanzie
          {companyName && <span className="text-[10px] text-indigo-700 font-normal normal-case tracking-normal">· Compagnia: {companyName}</span>}
          {/* 🆕 16/07/2026 — Indicatore Importi annuali (bene visibile) */}
          <span
            className="text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-amber-900 border border-amber-500 rounded px-2 py-0.5 ml-1 shadow-sm"
            title="Tutti gli importi in questa sezione sono espressi su base annuale"
            data-testid={`${testIdPrefix}-annual-badge`}
          >📅 Importi annuali</span>
        </Label>
        <div className="flex items-center gap-2 flex-wrap">
          {/* 🆕 15/07/2026 — Toggle: escludi Vendita dal Totale Generale Premio Lordo */}
          <label
            className="inline-flex items-center gap-1.5 text-[11px] text-indigo-800 bg-white border border-indigo-200 rounded-full px-2.5 py-1 cursor-pointer hover:bg-indigo-50 transition-colors select-none"
            title="Se attivato, il campo 'Vendita' delle garanzie non verrà sommato al Totale Generale Premio Lordo (utile per confrontare 'solo premio' vs 'premio + vendita'). Preferenza salvata nel browser."
            data-testid={`${testIdPrefix}-toggle-exclude-sell`}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
              checked={excludeSellFromTotal}
              onChange={(e) => setExcludeSellFromTotal(e.target.checked)}
            />
            <span className="font-medium">Escludi Vendita dal totale</span>
          </label>
          {/* 🆕 15/07/2026 — Aggiungi Prodotto dal Catalogo Prodotti (non richiede compagnia sulla polizza) */}
          <Button
            type="button"
            data-testid={`${testIdPrefix}-guarantees-recalc-btn`}
            size="sm"
            variant="outline"
            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            disabled={(coverages || []).length === 0}
            title="Ricalcola Premio Netto, Imposte e SSN di tutte le righe partendo dal Premio Lordo e dalle % di listino"
            onClick={() => {
              // 🆕 Feb 2026 — Ricalcola per ogni riga: net_premium = lordo / (1 + (imposte% + ssn%)/100)
              // Imposte/SSN amounts sono derivati in render da net_premium × %.
              let touched = 0;
              const next = (coverages || []).map((c) => {
                const gross = parseFloat(c.premium);
                if (!Number.isFinite(gross) || gross <= 0) return c;
                const newNet = computeNet(gross, c.imposte_percent, c.ssn_percent);
                if (newNet == null) return c;
                touched += 1;
                return { ...c, net_premium: newNet };
              });
              onChange(next);
              toast.success(`Ricalcolo eseguito: ${touched} righe aggiornate`);
            }}
          >
            <Sparkles className="h-4 w-4 mr-1.5" /> Ricalcola importi
          </Button>
          <Button
            type="button"
            data-testid={`${testIdPrefix}-products-add-btn`}
            size="sm"
            variant="outline"
            className="border-purple-500 text-purple-700 hover:bg-purple-50"
            disabled={(products || []).length === 0}
            title={(products || []).length === 0 ? "Nessun prodotto nel catalogo — vai a Catalogo prodotti per crearne" : "Aggiungi un prodotto dal Catalogo Prodotti"}
            onClick={() => { setProductsSelected(new Set()); setProductsPickerOpen(true); }}
          >
            <Package className="h-4 w-4 mr-1.5" /> Aggiungi Prodotto
          </Button>
          <Button
            type="button"
            data-testid={`${testIdPrefix}-guarantees-add-btn`}
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            disabled={!canAdd}
            title={canAdd ? "Aggiungi una garanzia dal listino compagnia" : "Seleziona prima una compagnia sulla polizza"}
            onClick={openPicker}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Aggiungi Garanzia
          </Button>
        </div>
      </div>

      {!canAdd && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800">
          ⚠ Seleziona la compagnia della polizza per gestire le garanzie.
        </div>
      )}

      {canAdd && (
        <div className="bg-white border border-indigo-200 rounded-md overflow-hidden">
          <div className="divide-y divide-indigo-100" data-testid={`${testIdPrefix}-guarantees-tbody`}>
            {(coverages || []).length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-400">
                Nessuna garanzia aggiunta. Clicca <strong>Aggiungi Garanzia</strong> per iniziare.
              </div>
            ) : (
              // 🆕 16/07/2026 — DnD Kit context per drag&drop delle garanzie
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={(evt) => {
                  const { active, over } = evt;
                  if (!over || active.id === over.id) return;
                  const oldIdx = Number(String(active.id).replace("guarantee-row-", ""));
                  const newIdx = Number(String(over.id).replace("guarantee-row-", ""));
                  if (Number.isNaN(oldIdx) || Number.isNaN(newIdx)) return;
                  onChange(arrayMove(coverages || [], oldIdx, newIdx));
                }}
              >
                <SortableContext
                  items={(coverages || []).map((_, i) => `guarantee-row-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
            {(coverages || []).map((c, idx) => {
              // 🆕 Calcoli per singola garanzia
              const netVal = parseFloat(c.net_premium) || 0;
              const impostePct = parseFloat(c.imposte_percent);
              const ssnPct = parseFloat(c.ssn_percent);
              const impVal = Number.isFinite(impostePct) && netVal > 0 ? Math.round(netVal * impostePct) / 100 : null;
              const ssnVal = Number.isFinite(ssnPct) && netVal > 0 ? Math.round(netVal * ssnPct) / 100 : null;
              // 🆕 15/07/2026 — Visibilità Acquisto/Vendita per singola riga.
              const showBuy = c.has_buy_field !== false;
              const showSell = c.has_sell_field !== false;
              // 🆕 15/07/2026 — Calcolo provvigione per riga con override dal link broker↔compagnia.
              // Priorità: 1) fixed_cost (override) → provv = fixed_cost, 2) commission_percent (override), 3) commissionPercent globale.
              const override = c.company_guarantee_id ? linkGuaranteesMap[c.company_guarantee_id] : null;
              let effectivePct = defaultRowPct;
              let provv = 0;
              let provvSource = "default";
              if (override) {
                if (override.fixed_cost != null && Number(override.fixed_cost) > 0) {
                  provv = Number(override.fixed_cost);
                  effectivePct = null; // costo fisso, non %
                  provvSource = "fixed";
                } else if (override.commission_percent != null) {
                  effectivePct = Number(override.commission_percent);
                  provv = netVal * effectivePct / 100;
                  provvSource = "pct-override";
                } else {
                  provv = netVal * effectivePct / 100;
                }
              } else {
                provv = netVal * effectivePct / 100;
              }
              const feeAg = provv * (agencyPctOfCommission || 0) / 100;
              const feeCollab = feeAg * (collabPctOfAgency || 0) / 100;
              // 🆕 15/07/2026 — Aggiungi Fee accessorie all'ammontare della Provv. Agenzia visualizzato per riga
              const rowFeeBrokerSplit = parseFloat(c.broker_fee_to_split) || 0;
              const rowExtraFees = parseFloat(c.extra_fees) || 0;
              const rowBrokerFixed = parseFloat(c.broker_fixed_fee) || 0;
              // 🆕 15/07/2026 (aggiornato) — Comp. Fisso Broker (broker_fixed_fee) va 100% all'agenzia
              const rowAgencyFromFees = (rowFeeBrokerSplit * (feePct || 0) / 100) + rowExtraFees + rowBrokerFixed;
              const feeAgWithFees = feeAg + rowAgencyFromFees;
              const feeCollabWithFees = feeAgWithFees * (collabPctOfAgency || 0) / 100;
              const LBL = "block text-[9px] uppercase tracking-wider font-semibold text-slate-500 mb-0.5";
              return (
                <SortableGuaranteeRow
                  key={`${c.code}-${idx}`}
                  id={`guarantee-row-${idx}`}
                  className={`px-3 py-2 ${idx % 2 === 0 ? "bg-white" : "bg-indigo-50/50"}`}
                  testId={`${testIdPrefix}-guarantee-row-${idx}`}
                >
                  {({ attributes, listeners }) => (
                  <>
                  {/* Header riga: nome + delete */}
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* 🆕 16/07/2026 — Drag handle per drag & drop garanzie */}
                      <button
                        type="button"
                        {...attributes}
                        {...listeners}
                        data-testid={`${testIdPrefix}-guarantee-drag-${idx}`}
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded p-1 touch-none"
                        title="Trascina per riordinare"
                        aria-label="Trascina per riordinare"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      {/* 🆕 Feb 2026 — Pallino rosso: garanzia estratta non collegata al listino compagnia */}
                      {c._unmatched && (
                        <span
                          data-testid={`${testIdPrefix}-guarantee-unmatched-${idx}`}
                          className="inline-block h-2.5 w-2.5 rounded-full bg-rose-500 shadow-[0_0_0_2px_rgba(244,63,94,0.25)] flex-shrink-0"
                          title="Garanzia non collegata al listino compagnia. Usa la tendina «Collega a…» per associarla e opzionalmente salvarla come alias."
                          aria-label="Garanzia non collegata"
                        />
                      )}
                      {/* 🆕 Feb 2026 — Menù «Collega a…» in-line quando la garanzia non è agganciata */}
                      {c._unmatched && companyGuarantees.length > 0 && (
                        <select
                          data-testid={`${testIdPrefix}-guarantee-link-select-${idx}`}
                          className="h-6 px-1.5 text-[11px] border border-rose-300 bg-rose-50 text-rose-700 rounded font-medium focus:outline-none focus:ring-2 focus:ring-rose-300 max-w-[180px]"
                          defaultValue=""
                          onChange={async (e) => {
                            const gid = e.target.value;
                            if (!gid) return;
                            const master = companyGuarantees.find((g) => g.id === gid);
                            if (!master) return;
                            const originalName = (c.extracted_name || c.name || "").trim();
                            // Aggancia riga → nome canonico, propaga imposte/ssn, salva extracted_name se non c'è, spegni flag
                            onChange((coverages || []).map((row, i) => (
                              i === idx ? ({
                                ...row,
                                name: master.name,
                                extracted_name: row.extracted_name || (originalName && originalName !== master.name ? originalName : null),
                                _unmatched: false,
                                company_guarantee_id: master.id,
                                imposte_percent: master.imposte_percent != null ? Number(master.imposte_percent) : row.imposte_percent,
                                ssn_percent: master.ssn_percent != null ? Number(master.ssn_percent) : row.ssn_percent,
                              }) : row
                            )));
                            toast.success(`Collegata a ${master.name}`);
                            // Proponi di salvare il nome originale come alias sulla master
                            if (originalName && originalName.toLowerCase() !== (master.name || "").toLowerCase()) {
                              const already = Array.isArray(master.aliases) && master.aliases.some((a) => (a || "").toLowerCase() === originalName.toLowerCase());
                              if (!already && window.confirm(`Salvare "${originalName}" come alias per "${master.name}"?\n\nLa prossima estrazione riconoscerà automaticamente questa dicitura.`)) {
                                try {
                                  const nextAliases = [...(master.aliases || []), originalName];
                                  await api.put(`/companies/${companyId}/guarantees/${master.id}`, { ...master, aliases: nextAliases });
                                  await load();
                                  toast.success("Alias salvato sulla voce di listino");
                                } catch (err) {
                                  toast.error(formatApiError(err));
                                }
                              }
                            }
                          }}
                        >
                          <option value="">🔗 Collega a…</option>
                          {companyGuarantees.map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                      )}
                      <span className="font-semibold text-sm text-slate-800">{c.name}</span>
                      {/* 🆕 Feb 2026 — Chip: etichetta originale estratta dall'AI (se il nome è stato rinominato dal resolver) */}
                      {c.extracted_name && c.extracted_name !== c.name && (
                        <span
                          data-testid={`${testIdPrefix}-guarantee-extracted-name-${idx}`}
                          className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1 max-w-[280px]"
                          title={`Etichetta originale estratta dal PDF: "${c.extracted_name}". Il nome è stato allineato al listino compagnia.`}
                        >
                          <FileText className="h-2.5 w-2.5 flex-shrink-0" />
                          <span className="truncate normal-case tracking-normal">{c.extracted_name}</span>
                        </span>
                      )}
                      {/* 🆕 15/07/2026 — Descrizione provvigione applicata per questa garanzia */}
                      {(provv > 0 || effectivePct != null) && (
                        <span className="text-[11px] text-blue-700 font-medium" data-testid={`${testIdPrefix}-guarantee-commission-desc-${idx}`} title={provvSource === "fixed" ? "Costo fisso da accordo broker" : provvSource === "pct-override" ? "% Override da accordo broker" : "Provvigione applicata"}>
                          {provvSource === "fixed"
                            ? `Provv. FISSA · ${formatCurrency(provv)}`
                            : `Provv. ${effectivePct}% · ${provv > 0 ? formatCurrency(provv) : "—"}`}
                          {provvSource !== "default" && <span className="ml-1 text-[9px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1 py-0.5 rounded">accordo broker</span>}
                        </span>
                      )}
                      {/* 🆕 15/07/2026 — Margine (auto) per prodotti dal Catalogo */}
                      {c.product_id && canSeeMargins && (() => {
                        const buy = parseFloat(c.buy_cost);
                        const sell = parseFloat(c.sell_cost);
                        if (!Number.isFinite(buy) || !Number.isFinite(sell)) return null;
                        const margin = Math.round((sell - buy) * 100) / 100;
                        const pct = buy > 0 ? Math.round((margin / buy) * 1000) / 10 : null;
                        return (
                          <span className="text-[11px] text-emerald-700 font-medium" data-testid={`${testIdPrefix}-guarantee-margin-${idx}`} title="Margine calcolato = Vendita − Acquisto">
                            💰 Margine (auto): <strong>{formatCurrency(margin)}</strong>
                            {pct != null && <span className="ml-1 text-[9px] text-emerald-600">({pct}%)</span>}
                          </span>
                        );
                      })()}
                      {c.product_id && !c.company_guarantee_id && <span className="text-[9px] uppercase tracking-wider bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded flex items-center gap-1" title="Da catalogo prodotti"><Package className="h-2.5 w-2.5" /> prodotto</span>}
                      {!c.company_guarantee_id && !c.product_id && <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" title="Non collegata al listino compagnia">custom</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      {/* 🆕 16/07/2026 — Ordinamento manuale garanzie (frecce ↑/↓) */}
                      <button
                        type="button"
                        data-testid={`${testIdPrefix}-guarantee-move-up-${idx}`}
                        onClick={() => moveAt(idx, -1)}
                        disabled={idx === 0}
                        className="text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Sposta questa garanzia su"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid={`${testIdPrefix}-guarantee-move-down-${idx}`}
                        onClick={() => moveAt(idx, 1)}
                        disabled={idx === (coverages || []).length - 1}
                        className="text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Sposta questa garanzia giù"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid={`${testIdPrefix}-guarantee-remove-${idx}`}
                        onClick={() => removeAt(idx)}
                        className="text-rose-600 hover:bg-rose-50 rounded p-1"
                        title="Rimuovi garanzia"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {/* Riga 1: TUTTI I CAMPI DI INPUT (editabili) */}
                  <div className="grid gap-2 items-end" style={{ gridTemplateColumns: `repeat(${canSeeMargins ? 9 : 8}, minmax(0, 1fr))` }}>
                    <div>
                      <span className={LBL}>Premio Lordo €</span>
                      <MoneyInput
                        data-testid={`${testIdPrefix}-guarantee-premium-${idx}`}
                        className="h-8 text-xs"
                        value={c.premium ?? ""}
                        onChange={(v) => updatePremium(idx, v)}
                        placeholder="0,00"
                      />
                    </div>
                    <div className="bg-emerald-50/60 rounded px-1 pt-0.5">
                      <span className={`${LBL} text-emerald-700`}>Premio Netto € <span className="normal-case text-[8px] font-normal">(bidir.)</span></span>
                      <MoneyInput
                        data-testid={`${testIdPrefix}-guarantee-net-${idx}`}
                        className="h-8 text-xs font-semibold text-emerald-700"
                        value={c.net_premium ?? ""}
                        onChange={(v) => updateNet(idx, v)}
                        placeholder="0,00"
                      />
                    </div>
                    {canSeeMargins && (
                      showBuy ? (
                        <div className="bg-amber-50/60 rounded px-1 pt-0.5">
                          <span className={`${LBL} text-amber-800`}>Acquisto €</span>
                          <MoneyInput
                            data-testid={`${testIdPrefix}-guarantee-buy-${idx}`}
                            className="h-8 text-xs"
                            value={c.buy_cost ?? ""}
                            onChange={(v) => updateField(idx, "buy_cost", v)}
                            placeholder="0,00"
                          />
                        </div>
                      ) : (
                        <div className="opacity-30 pt-0.5" title="Campo Acquisto non abilitato per questa garanzia">
                          <span className={LBL}>Acquisto €</span>
                          <div className="h-8 flex items-center justify-center text-[10px] text-slate-400 border border-dashed border-slate-200 rounded">n/a</div>
                        </div>
                      )
                    )}
                    {showSell ? (
                      <div className="bg-amber-50/60 rounded px-1 pt-0.5">
                        <span className={`${LBL} text-amber-800`}>Vendita €</span>
                        <MoneyInput
                          data-testid={`${testIdPrefix}-guarantee-sell-${idx}`}
                          className="h-8 text-xs"
                          value={c.sell_cost ?? ""}
                          onChange={(v) => updateField(idx, "sell_cost", v)}
                          placeholder="0,00"
                        />
                      </div>
                    ) : (
                      <div className="opacity-30 pt-0.5" title="Campo Vendita non abilitato per questa garanzia">
                        <span className={LBL}>Vendita €</span>
                        <div className="h-8 flex items-center justify-center text-[10px] text-slate-400 border border-dashed border-slate-200 rounded">n/a</div>
                      </div>
                    )}
                    <div className="bg-orange-50/60 rounded px-1 pt-0.5">
                      <span className={`${LBL} text-orange-800`} title="Fee inserite dal Broker/Agenzia — da ripartire">Fee Broker (ripart.)</span>
                      <MoneyInput
                        data-testid={`${testIdPrefix}-guarantee-broker-fee-split-${idx}`}
                        className="h-8 text-xs"
                        value={c.broker_fee_to_split ?? ""}
                        onChange={(v) => updateField(idx, "broker_fee_to_split", v)}
                        placeholder="0,00"
                      />
                    </div>
                    <div className="bg-orange-50/60 rounded px-1 pt-0.5">
                      <span className={`${LBL} text-orange-800`} title="Spese di gestione — 100% broker">Spese Gest. Broker €</span>
                      <MoneyInput
                        data-testid={`${testIdPrefix}-guarantee-broker-mgmt-${idx}`}
                        className="h-8 text-xs"
                        value={c.broker_management_cost ?? ""}
                        onChange={(v) => updateField(idx, "broker_management_cost", v)}
                        placeholder="0,00"
                      />
                    </div>
                    <div className="bg-orange-50/60 rounded px-1 pt-0.5">
                      <span className={`${LBL} text-orange-800`} title="Compenso fisso pattuito col broker/agenzia — SOLO PROVVIGIONI (non entra nel totale polizza)">Comp. Fisso dal Broker €</span>
                      <MoneyInput
                        data-testid={`${testIdPrefix}-guarantee-broker-fixed-${idx}`}
                        className="h-8 text-xs"
                        value={c.broker_fixed_fee ?? ""}
                        onChange={(v) => updateField(idx, "broker_fixed_fee", v)}
                        placeholder="0,00"
                      />
                    </div>
                    <div className="bg-slate-50 rounded px-1 pt-0.5">
                      <span className={`${LBL} text-slate-700`}>Extra Fees €</span>
                      <MoneyInput
                        data-testid={`${testIdPrefix}-guarantee-extra-fees-${idx}`}
                        className="h-8 text-xs"
                        value={c.extra_fees ?? ""}
                        onChange={(v) => updateField(idx, "extra_fees", v)}
                        placeholder="0,00"
                      />
                    </div>
                    {/* 🆕 16/07/2026 — Spacer allineato al nuovo "Fee Agenzia €" nella riga 2 */}
                    <div></div>
                  </div>
                  {/* Riga 2: CAMPI CALCOLATI (somme/derivati) — sotto gli input, allineati */}
                  <div className="grid gap-2 items-end mt-2 pt-2 border-t border-dashed border-slate-200" style={{ gridTemplateColumns: `repeat(${canSeeMargins ? 9 : 8}, minmax(0, 1fr))` }}>
                    {/* Sotto Premio Lordo: vuoto */}
                    <div></div>
                    {/* Sotto Premio Netto: Imposte € (calc) */}
                    <div className="text-right pr-1">
                      <span className={LBL}>Imposte €</span>
                      <div className="h-8 flex flex-col items-end justify-center leading-none text-slate-700" data-testid={`${testIdPrefix}-guarantee-imposte-${idx}`}>
                        <span className="text-xs tabular-nums font-semibold">{impVal != null ? formatCurrency(impVal) : "—"}</span>
                        {Number.isFinite(impostePct) && (
                          <span className="text-[9px] text-slate-500 mt-0.5">{impostePct}%</span>
                        )}
                      </div>
                    </div>
                    {/* SSN € */}
                    <div className="text-right pr-1">
                      <span className={LBL}>SSN €</span>
                      <div className="h-8 flex flex-col items-end justify-center leading-none text-slate-700" data-testid={`${testIdPrefix}-guarantee-ssn-${idx}`}>
                        <span className="text-xs tabular-nums font-semibold">{ssnVal != null ? formatCurrency(ssnVal) : "—"}</span>
                        {Number.isFinite(ssnPct) && (
                          <span className="text-[9px] text-slate-500 mt-0.5">{ssnPct}%</span>
                        )}
                      </div>
                    </div>
                    {/* spacer sotto Acquisto/Vendita */}
                    {canSeeMargins && <div></div>}
                    <div></div>
                    {/* 🗑️ 16/07/2026 — Rimossa colonna "Provv. garanzia (X%)" su richiesta utente */}
                    <div></div>
                    <div className="bg-blue-50/60 rounded px-1 py-0.5 text-right">
                      <span className={`${LBL} text-blue-700`}>Provv. Agenzia € <span className="normal-case text-[8px] font-normal">({agencyPctOfCommission}%)</span></span>
                      {/* 🆕 16/07/2026 — SOLO provvigione agenzia (senza fee accessorie) */}
                      <div className="h-8 flex items-center justify-end text-xs tabular-nums font-semibold text-blue-800" data-testid={`${testIdPrefix}-guarantee-fee-agenzia-${idx}`}>
                        {feeAg > 0 ? formatCurrency(feeAg) : "—"}
                      </div>
                    </div>
                    {/* 🆕 16/07/2026 — Fee agenzia (sommatoria specifiche): Fee Broker ripart. quota agenzia + Extra Fees (100%) + Comp. Fisso Broker (100%) */}
                    {(() => {
                      const _feeBrokerAgencyShare = rowFeeBrokerSplit * (feePct || 0) / 100;
                      return (
                        <div className="bg-amber-50/60 rounded px-1 py-0.5 text-right">
                          <span className={`${LBL} text-amber-700`}>Fee Agenzia €</span>
                          <div
                            className="h-8 flex items-center justify-end text-xs tabular-nums font-semibold text-amber-800"
                            data-testid={`${testIdPrefix}-guarantee-fee-agenzia-specific-${idx}`}
                            title={rowAgencyFromFees > 0
                              ? [
                                  `Fee Broker ripart. (${formatCurrency(rowFeeBrokerSplit)} × ${feePct || 0}%) = ${formatCurrency(_feeBrokerAgencyShare)}`,
                                  `+ Extra Fees (100% ag.) = ${formatCurrency(rowExtraFees)}`,
                                  `+ Comp. Fisso Broker (100% ag.) = ${formatCurrency(rowBrokerFixed)}`,
                                  `TOTALE = ${formatCurrency(rowAgencyFromFees)}`,
                                ].join("\n")
                              : "Nessuna fee accessoria configurata per questa garanzia"}
                          >
                            {rowAgencyFromFees > 0 ? formatCurrency(rowAgencyFromFees) : "—"}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="bg-rose-50/60 rounded px-1 py-0.5 text-right">
                      <span className={`${LBL} text-rose-700`}>Fee Collab. € <span className="normal-case text-[8px] font-normal">({collabPctOfAgency}%)</span></span>
                      <div className="h-8 flex items-center justify-end text-xs tabular-nums font-semibold text-rose-800" data-testid={`${testIdPrefix}-guarantee-fee-collab-${idx}`}>
                        {feeCollabWithFees > 0 ? formatCurrency(feeCollabWithFees) : "—"}
                      </div>
                    </div>
                  </div>
                  </>
                  )}
                </SortableGuaranteeRow>
              );
            })}
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Totale generale — evidenziato in grande e grassetto + riga riepilogo provvigioni */}
          {(() => {
            const totNet = (coverages || []).reduce((s, c) => s + (parseFloat(c.net_premium) || 0), 0);
            // 🆕 Feb 2026 — Multi-Garanzia attiva? (bonus% > 0 && >1 garanzia con net > 0)
            const _bonusPct = parseFloat(multiGuaranteeBonusPercent) || 0;
            const _netsGt0 = (coverages || [])
              .map((c) => parseFloat(c.net_premium) || 0)
              .filter((n) => n > 0);
            const multiGuaranteeActive = _bonusPct > 0 && _netsGt0.length > 1;
            // 🆕 15/07/2026 — Totali provvigioni calcolati per riga con override link broker↔compagnia
            let totProvv = 0;
            if (multiGuaranteeActive) {
              // Modalità multi-garanzia: totProvv = bonus% × Σ net (sostituisce calcolo per-riga)
              totProvv = _netsGt0.reduce((s, n) => s + n, 0) * _bonusPct / 100;
            } else {
              for (const c of (coverages || [])) {
                const n = parseFloat(c.net_premium) || 0;
                const ov = c.company_guarantee_id ? linkGuaranteesMap[c.company_guarantee_id] : null;
                if (ov && ov.fixed_cost != null && Number(ov.fixed_cost) > 0) {
                  totProvv += Number(ov.fixed_cost);
                } else if (ov && ov.commission_percent != null) {
                  totProvv += n * Number(ov.commission_percent) / 100;
                } else {
                  totProvv += n * defaultRowPct / 100;
                }
              }
            }
            const totFeeAg = totProvv * (agencyPctOfCommission || 0) / 100;
            // 🆕 15/07/2026 — Somme delle fee/compensi accessori
            const sum = (field) => (coverages || []).reduce((s, c) => s + (parseFloat(c[field]) || 0), 0);
            const totBrokerFeeSplit = sum("broker_fee_to_split");
            const totBrokerMgmt = sum("broker_management_cost");
            const totBrokerFixed = sum("broker_fixed_fee");
            const totExtraFees = sum("extra_fees");
            // Quota agenzia dalle Fee Broker (ripartite) + Extra Fees (100% agenzia) + Comp. Fisso Broker (100% agenzia)
            const totAgencyFromBrokerFee = totBrokerFeeSplit * (feePct || 0) / 100;
            const totFeeAgWithFees = totFeeAg + totAgencyFromBrokerFee + totExtraFees + totBrokerFixed;
            const totFeeCollab = totFeeAgWithFees * (collabPctOfAgency || 0) / 100;
            // 🆕 16/07/2026 — Margine totale = Σ (Vendita − Acquisto) sui prodotti dal catalogo (dove entrambi sono valorizzati)
            const totMargin = (coverages || []).reduce((s, c) => {
              if (!c.product_id) return s;
              const buy = parseFloat(c.buy_cost);
              const sell = parseFloat(c.sell_cost);
              if (!Number.isFinite(buy) || !Number.isFinite(sell)) return s;
              return s + (sell - buy);
            }, 0);
            const totBuy = (coverages || []).reduce((s, c) => {
              if (!c.product_id) return s;
              const buy = parseFloat(c.buy_cost);
              return Number.isFinite(buy) ? s + buy : s;
            }, 0);
            const totMarginPct = totBuy > 0 ? Math.round((totMargin / totBuy) * 1000) / 10 : null;
            return (
              <>
                <div className="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid={`${testIdPrefix}-guarantees-total-box`}>
                  <span className="text-xs uppercase tracking-wider font-semibold opacity-90">Totale Generale Premio Lordo ANNUALE</span>
                  <div className="flex flex-col items-end">
                    <span className="text-2xl sm:text-3xl font-bold tabular-nums" data-testid={`${testIdPrefix}-guarantees-total-value`}>
                      {formatCurrency(totale)}
                    </span>
                    {(totaleSell > 0 || totaleFeeBrokerSplit > 0 || totaleBrokerMgmt > 0 || totaleExtraFees > 0) && (
                      <span className="text-[10px] text-indigo-100 tabular-nums mt-0.5 text-right" data-testid={`${testIdPrefix}-guarantees-total-breakdown`}>
                        = Premi <strong className="text-white">{formatCurrency(totalePremiOnly)}</strong>
                        {totaleSell > 0 && !excludeSellFromTotal && (
                          <> + Vendita <strong className="text-white">{formatCurrency(totaleSell)}</strong></>
                        )}
                        {totaleFeeBrokerSplit > 0 && (
                          <> + Fee Broker (rip.) <strong className="text-white">{formatCurrency(totaleFeeBrokerSplit)}</strong></>
                        )}
                        {totaleBrokerMgmt > 0 && (
                          <> + Spese Gest. Broker <strong className="text-white">{formatCurrency(totaleBrokerMgmt)}</strong></>
                        )}
                        {totaleExtraFees > 0 && (
                          <> + Extra Fees <strong className="text-white">{formatCurrency(totaleExtraFees)}</strong></>
                        )}
                      </span>
                    )}
                    {totaleSell > 0 && excludeSellFromTotal && (
                      <span className="text-[10px] text-amber-200 tabular-nums mt-0.5" data-testid={`${testIdPrefix}-guarantees-total-excluded-sell`} title="Il campo 'Vendita' è escluso dal totale (toggle attivo)">
                        ⚠ Vendita esclusa: <strong className="text-white">{formatCurrency(totaleSell)}</strong>
                      </span>
                    )}
                  </div>
                </div>
                {(coverages || []).length > 0 && (
                  <>
                    <div className="bg-slate-50 border-t border-indigo-200 px-4 py-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid={`${testIdPrefix}-guarantees-commission-summary`}>
                      <div>
                        <div className="text-slate-500 uppercase text-[10px] tracking-wider">Netto (base provv.)</div>
                        <div className="font-bold text-emerald-700 tabular-nums text-base">{formatCurrency(totNet)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase text-[10px] tracking-wider">Provv. tot.</div>
                        <div className="font-bold text-blue-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-provv`}>{formatCurrency(totProvv)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase text-[10px] tracking-wider">Provv. Agenzia tot.</div>
                        <div className="font-bold text-blue-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-fee-agenzia`}>{formatCurrency(totFeeAg)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase text-[10px] tracking-wider">Fee Collab. tot.{collabName ? ` · ${collabName}` : ""}</div>
                        <div className="font-bold text-rose-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-fee-collab`}>{formatCurrency(totFeeCollab)}</div>
                      </div>
                    </div>
                    {/* 🆕 15/07/2026 — Riepilogo Fee/Compensi accessori + Provv. Agenzia con fee incluse */}
                    <div className="bg-orange-50/40 border-t border-indigo-200 px-4 py-2 grid grid-cols-2 sm:grid-cols-7 gap-3 text-xs" data-testid={`${testIdPrefix}-guarantees-fees-summary`}>
                      <div>
                        <div className="text-orange-700 uppercase text-[10px] tracking-wider" title="Fee inserite dal Broker/Agenzia — da ripartire">Fee Broker (rip.) tot.</div>
                        <div className="font-bold text-orange-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-broker-fee-split`}>{formatCurrency(totBrokerFeeSplit)}</div>
                      </div>
                      <div>
                        <div className="text-orange-700 uppercase text-[10px] tracking-wider" title="Spese di gestione — 100% broker">Spese Gest. tot.</div>
                        <div className="font-bold text-orange-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-broker-mgmt`}>{formatCurrency(totBrokerMgmt)}</div>
                      </div>
                      <div>
                        <div className="text-orange-700 uppercase text-[10px] tracking-wider" title="Compenso fisso pattuito col broker/agenzia">Comp. Fisso tot.</div>
                        <div className="font-bold text-orange-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-broker-fixed`}>{formatCurrency(totBrokerFixed)}</div>
                      </div>
                      <div>
                        <div className="text-slate-600 uppercase text-[10px] tracking-wider">Extra Fees tot.</div>
                        <div className="font-bold text-slate-800 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-extra-fees`}>{formatCurrency(totExtraFees)}</div>
                      </div>
                      {/* 🆕 16/07/2026 — Tot. Fee Agenzia = (Fee Broker × agencyPct%) + Extra Fees + Comp. Fisso Broker.
                          Diviso per numero rate → è esattamente il valore che compare in "Fee agenzia (€)" delle rate. */}
                      <div className="bg-amber-100 rounded px-2 py-1">
                        <div className="text-amber-800 uppercase text-[10px] tracking-wider" title="Somma delle sole fee accessorie che spettano all'agenzia (esclusi Provvig. e Spese Gest. Broker). Diviso per N rate = importo della cella 'Fee agenzia (€)' di ogni rata.">Tot. Fee Agenzia</div>
                        <div className="font-bold text-amber-900 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-fee-agenzia-only`}>{formatCurrency(totAgencyFromBrokerFee + totExtraFees + totBrokerFixed)}</div>
                        <div className="text-[9px] text-amber-700 leading-tight mt-0.5">
                          {totBrokerFeeSplit > 0 && (
                            <>Fee Broker × {feePct}% <strong>{formatCurrency(totAgencyFromBrokerFee)}</strong></>
                          )}
                          {totExtraFees > 0 && (
                            <> + Extra <strong>{formatCurrency(totExtraFees)}</strong></>
                          )}
                          {totBrokerFixed > 0 && (
                            <> + C. Fisso <strong>{formatCurrency(totBrokerFixed)}</strong></>
                          )}
                        </div>
                      </div>
                      {/* 🆕 16/07/2026 — Margine (auto) = Σ (Vendita − Acquisto) sui prodotti dal catalogo */}
                      {canSeeMargins && (
                        <div className="bg-emerald-100 rounded px-2 py-1">
                          <div className="text-emerald-800 uppercase text-[10px] tracking-wider" title="Somma dei margini calcolati sui prodotti dal Catalogo Prodotti (Vendita − Acquisto). Percentuale = margine / acquisto.">💰 Margine (auto)</div>
                          <div className="font-bold text-emerald-900 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-margin`}>
                            {formatCurrency(totMargin)}
                            {totMarginPct != null && (
                              <span className="ml-1 text-[10px] font-semibold text-emerald-700">({totMarginPct}%)</span>
                            )}
                          </div>
                          <div className="text-[9px] text-emerald-700 leading-tight mt-0.5">
                            = Σ (Vendita − Acquisto) prodotti
                          </div>
                        </div>
                      )}
                      <div className="bg-blue-100 rounded px-2 py-1 border-2 border-blue-500 shadow-sm">
                        <div className="text-blue-800 uppercase text-[10px] tracking-wider flex items-center gap-1 flex-wrap">
                          <span>Provv. Ag. (fee incl.)</span>
                          {multiGuaranteeActive && (
                            <span
                              data-testid={`${testIdPrefix}-multi-guarantee-badge`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 text-[9px] normal-case tracking-normal font-semibold"
                              title={`Modalità Multi-Garanzia attiva: ${formatCurrency(totProvv)} = ${_bonusPct}% × Σ netto (${formatCurrency(_netsGt0.reduce((s,n)=>s+n,0))}) → sostituisce il calcolo per-riga.`}
                            >
                              ⭐ Provvigione multigaranzia
                            </span>
                          )}
                        </div>
                        <div className="font-bold text-blue-900 tabular-nums text-base" data-testid={`${testIdPrefix}-tot-fee-agenzia-with-fees`}>{formatCurrency(totFeeAgWithFees)}</div>
                        <div className="text-[9px] text-blue-700 leading-tight mt-0.5" data-testid={`${testIdPrefix}-tot-fee-agenzia-with-fees-breakdown`}>
                          = Provv. Agenzia <strong>{formatCurrency(totFeeAg)}</strong>
                          {multiGuaranteeActive && (
                            <span className="text-amber-700"> (multi-garanzia: {_bonusPct}% × {formatCurrency(_netsGt0.reduce((s,n)=>s+n,0))})</span>
                          )}
                          {totBrokerFeeSplit > 0 && (
                            <> + Fee Broker (rip.) × {feePct}% <strong>{formatCurrency(totAgencyFromBrokerFee)}</strong></>
                          )}
                          {totExtraFees > 0 && (
                            <> + Extra Fees <strong>{formatCurrency(totExtraFees)}</strong></>
                          )}
                          {totBrokerFixed > 0 && (
                            <> + Comp. Fisso Broker <strong>{formatCurrency(totBrokerFixed)}</strong></>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* 🆕 20/07/2026 — Barra "Esclusioni Estratto Conto": permette di escludere Extra Fees / Comp. Fisso Broker dal totale dell'Estratto Conto SENZA modificare il dettaglio polizza. */}
                    {(typeof onExcludeExtraFeesFromStatementChange === "function" || typeof onExcludeBrokerFixedFromStatementChange === "function") && (
                      <div className="bg-amber-50 border-t border-amber-200 px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid={`${testIdPrefix}-statement-exclusions-bar`}>
                        <div className="text-[11px] uppercase tracking-wider text-amber-900 font-semibold inline-flex items-center gap-1.5">
                          <span className="text-base leading-none">🚫</span>
                          Esclusioni Estratto Conto
                          <span className="text-[9px] font-normal text-amber-700 normal-case tracking-normal">(non modificano il dettaglio polizza)</span>
                        </div>
                        {typeof onExcludeExtraFeesFromStatementChange === "function" && (
                          <label className="flex items-center gap-2 cursor-pointer select-none text-xs" title="Se attivo, le Extra Fees NON vengono conteggiate nella colonna 'Provv. Ag. (fee incl.)' dell'Estratto Conto. Il valore resta comunque visibile e conteggiato in questa vista di dettaglio.">
                            <input
                              type="checkbox"
                              checked={!!excludeExtraFeesFromStatement}
                              onChange={(e) => onExcludeExtraFeesFromStatementChange(e.target.checked)}
                              className="h-4 w-4 rounded border-amber-300 accent-amber-700"
                              data-testid={`${testIdPrefix}-exclude-extra-fees-from-statement`}
                            />
                            <span className="text-slate-700">Escludi <strong>Extra Fees</strong></span>
                            {totExtraFees > 0 && <span className="text-slate-400 tabular-nums">({formatCurrency(totExtraFees)})</span>}
                          </label>
                        )}
                        {typeof onExcludeBrokerFixedFromStatementChange === "function" && (
                          <label className="flex items-center gap-2 cursor-pointer select-none text-xs" title="Se attivo, il Comp. Fisso Broker NON viene conteggiato nella colonna 'Provv. Ag. (fee incl.)' dell'Estratto Conto. Il valore resta comunque visibile e conteggiato in questa vista di dettaglio.">
                            <input
                              type="checkbox"
                              checked={!!excludeBrokerFixedFromStatement}
                              onChange={(e) => onExcludeBrokerFixedFromStatementChange(e.target.checked)}
                              className="h-4 w-4 rounded border-amber-300 accent-amber-700"
                              data-testid={`${testIdPrefix}-exclude-broker-fixed-from-statement`}
                            />
                            <span className="text-slate-700">Escludi <strong>Comp. Fisso Broker</strong></span>
                            {totBrokerFixed > 0 && <span className="text-slate-400 tabular-nums">({formatCurrency(totBrokerFixed)})</span>}
                          </label>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (!o) setCreating(false); }}>
        <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-guarantees-picker`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <ShieldCheck className="h-5 w-5" /> Aggiungi garanzia alla polizza
              {companyName && <span className="text-xs font-normal text-slate-500">· {companyName}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!creating && (
              <>
                <p className="text-xs text-slate-600">
                  Seleziona una garanzia dal listino della compagnia. Le garanzie già aggiunte a questa polizza sono nascoste.
                </p>
                {loading ? (
                  <div className="text-center text-sm text-slate-500 py-6"><Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Caricamento…</div>
                ) : availableGuarantees.length === 0 ? (
                  <div className="text-center text-sm text-slate-500 py-6">
                    {companyGuarantees.length === 0
                      ? "Nessuna garanzia configurata per questa compagnia."
                      : "Tutte le garanzie di questa compagnia sono già in polizza."}
                  </div>
                ) : (
                  <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                    {availableGuarantees.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        data-testid={`${testIdPrefix}-picker-guarantee-${g.id}`}
                        onClick={() => { addGuaranteeToPolicy(g); setPickerOpen(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-slate-800">{g.name}</span>
                          {/* 🆕 15/07/2026 — % provvigione (override link broker↔compagnia o default polizza) */}
                          {(() => {
                            const ov = linkGuaranteesMap[g.id];
                            if (ov && ov.fixed_cost != null && Number(ov.fixed_cost) > 0) {
                              return <span className="text-[11px] text-blue-700 font-medium">Provv. FISSA {formatCurrency(ov.fixed_cost)} <span className="text-[9px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1 py-0.5 rounded ml-1">accordo broker</span></span>;
                            }
                            const pct = (ov && ov.commission_percent != null) ? Number(ov.commission_percent) : defaultRowPct;
                            const isOverride = ov && ov.commission_percent != null;
                            return <span className="text-[11px] text-blue-700 font-medium">Provv. {pct}%{isOverride && <span className="text-[9px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1 py-0.5 rounded ml-1">accordo broker</span>}</span>;
                          })()}
                        </div>
                        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          <span>Imposte: <strong>{g.imposte_percent != null ? `${g.imposte_percent}%` : "—"}</strong></span>
                          <span>SSN: <strong>{g.ssn_percent != null ? `${g.ssn_percent}%` : "—"}</strong></span>
                          {canSeeMargins && <span>Acquisto: <strong>{g.buy_price != null ? formatCurrency(g.buy_price) : "—"}</strong></span>}
                          <span>Vendita: <strong className="text-emerald-700">{g.sell_price != null ? formatCurrency(g.sell_price) : "—"}</strong></span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="pt-2 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={`${testIdPrefix}-picker-create-new-btn`}
                    onClick={() => setCreating(true)}
                    className="w-full border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                  >
                    <Sparkles className="h-4 w-4 mr-1.5" /> ✏️ Non è nell&apos;elenco? Crea una nuova garanzia per la compagnia
                  </Button>
                </div>
              </>
            )}
            {creating && (
              <div className="space-y-3 border border-indigo-300 rounded-md bg-indigo-50/30 p-3">
                <p className="text-xs text-indigo-900 font-semibold">
                  Nuova garanzia · verrà salvata nel listino di <strong>{companyName || "questa compagnia"}</strong> e aggiunta subito alla polizza.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Nome garanzia *</Label>
                    <Input
                      data-testid={`${testIdPrefix}-picker-new-name`}
                      value={newForm.name}
                      onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                      placeholder="Es. Kasko, Cristalli, Assistenza stradale"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label>Imposte (%)</Label>
                    <Input
                      data-testid={`${testIdPrefix}-picker-new-imposte`}
                      type="number" step="0.01" min="0" max="100"
                      value={newForm.imposte_percent}
                      onChange={(e) => setNewForm({ ...newForm, imposte_percent: e.target.value })}
                      placeholder="Es. 12.5"
                    />
                  </div>
                  <div>
                    <Label>SSN (%)</Label>
                    <Input
                      data-testid={`${testIdPrefix}-picker-new-ssn`}
                      type="number" step="0.01" min="0" max="100"
                      value={newForm.ssn_percent}
                      onChange={(e) => setNewForm({ ...newForm, ssn_percent: e.target.value })}
                      placeholder="Es. 10.5"
                    />
                  </div>
                  {canSeeMargins && (
                    <div>
                      <Label>Acquisto (€)</Label>
                      <Input
                        data-testid={`${testIdPrefix}-picker-new-buy`}
                        type="number" step="0.01" min="0"
                        value={newForm.buy_price}
                        onChange={(e) => setNewForm({ ...newForm, buy_price: e.target.value })}
                        placeholder="Es. 100.00"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Vendita (€)</Label>
                    <Input
                      data-testid={`${testIdPrefix}-picker-new-sell`}
                      type="number" step="0.01" min="0"
                      value={newForm.sell_price}
                      onChange={(e) => setNewForm({ ...newForm, sell_price: e.target.value })}
                      placeholder="Es. 150.00"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Note <span className="text-xs text-slate-400 font-normal">(facoltativo)</span></Label>
                    <Input
                      data-testid={`${testIdPrefix}-picker-new-notes`}
                      value={newForm.notes}
                      onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {creating ? (
              <>
                <Button variant="outline" onClick={() => setCreating(false)} disabled={savingNew}>Torna al listino</Button>
                <Button
                  data-testid={`${testIdPrefix}-picker-save-new`}
                  onClick={saveNewGuarantee}
                  disabled={savingNew || !newForm.name.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {savingNew ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Crea e aggiungi
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setPickerOpen(false)}>Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 15/07/2026 — Picker Prodotti dal Catalogo Prodotti */}
      <Dialog open={productsPickerOpen} onOpenChange={setProductsPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-products-picker`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Package className="h-5 w-5" /> Aggiungi prodotti dal catalogo
              {companyName && <span className="text-xs font-normal text-slate-500">· {companyName}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Seleziona uno o più prodotti dal catalogo della compagnia. I prodotti già in polizza sono nascosti.
            </p>
            {(() => {
              // Filtro prodotti: dal Catalogo Prodotti (attivi) — mostra tutti quelli non già in polizza.
              // La compagnia del prodotto è mostrata come badge informativo.
              const usedProductIds = new Set((coverages || []).map((c) => c.product_id).filter(Boolean));
              const usedNames = new Set((coverages || []).map((c) => (c.name || "").trim().toLowerCase()).filter(Boolean));
              const available = (products || []).filter((p) => {
                if (!p) return false;
                if (p.is_active === false) return false;
                if (usedProductIds.has(p.id)) return false;
                if (usedNames.has((p.name || "").trim().toLowerCase())) return false;
                return true;
              });
              if ((products || []).length === 0) {
                return <div className="text-center text-sm text-slate-500 py-6">Nessun prodotto nel catalogo. Vai a <strong>Catalogo prodotti</strong> nel menu per crearne uno.</div>;
              }
              if (available.length === 0) {
                return <div className="text-center text-sm text-slate-500 py-6">Tutti i prodotti del catalogo sono già in polizza.</div>;
              }
              // Ordinamento: prodotti della stessa compagnia della polizza per primi, poi per nome
              available.sort((a, b) => {
                const aMatch = companyId && a.company_id === companyId ? 0 : 1;
                const bMatch = companyId && b.company_id === companyId ? 0 : 1;
                if (aMatch !== bMatch) return aMatch - bMatch;
                return (a.name || "").localeCompare(b.name || "");
              });
              return (
                <div className="max-h-[50vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {available.map((p) => {
                    const checked = productsSelected.has(p.id);
                    const sameCompany = companyId && p.company_id === companyId;
                    return (
                      <label
                        key={p.id}
                        data-testid={`${testIdPrefix}-products-picker-item-${p.id}`}
                        className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-purple-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 accent-purple-600"
                          checked={checked}
                          onChange={() => {
                            setProductsSelected((prev) => {
                              const s = new Set(prev);
                              if (s.has(p.id)) s.delete(p.id); else s.add(p.id);
                              return s;
                            });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-800 flex items-center gap-2 flex-wrap">
                            {p.code && <span className="text-[10px] text-purple-700 tabular-nums bg-purple-100 px-1 rounded">{p.code}</span>}
                            {p.name}
                            {p.ramo && <span className="text-[10px] text-slate-500 uppercase">{p.ramo}</span>}
                            {p.company_name && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${sameCompany ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} title={sameCompany ? "Prodotto della stessa compagnia" : "Prodotto di altra compagnia"}>
                                🏢 {p.company_name}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                            {p.default_commission_percent != null && <span>Provv. default: <strong>{p.default_commission_percent}%</strong></span>}
                            {p.default_broker_commission_percent != null && <span>Broker: <strong>{p.default_broker_commission_percent}%</strong></span>}
                            {canSeeMargins && p.buy_cost != null && <span>Acquisto: <strong>{formatCurrency(p.buy_cost)}</strong></span>}
                            {p.sell_cost != null && <span>Vendita: <strong className="text-emerald-700">{formatCurrency(p.sell_cost)}</strong></span>}
                          </div>
                          {p.description && <div className="text-[11px] text-slate-500 mt-0.5 italic">{p.description}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductsPickerOpen(false)}>Annulla</Button>
            <Button
              data-testid={`${testIdPrefix}-products-picker-add-selected`}
              disabled={productsSelected.size === 0}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => {
                const toAdd = (products || []).filter((p) => productsSelected.has(p.id));
                for (const p of toAdd) addProductToPolicy(p);
                setProductsPickerOpen(false);
                setProductsSelected(new Set());
                toast.success(`${toAdd.length} prodott${toAdd.length === 1 ? "o aggiunto" : "i aggiunti"} alla polizza`);
              }}
            >
              Aggiungi selezionati ({productsSelected.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PolicyGuaranteesEditor;
